/**
 * conde-shop-by.js
 * Progressive Shop-By controller for /pages/shop-by-{method,shade,color,texture,length}.
 *
 * - Reads existing dimension selections from URL params.
 * - Hides tiles whose value has no matching variant in the narrowed catalog.
 * - Rewrites tile hrefs to carry selection state forward (cascade), or to
 *   land on a product detail page with ?variant=<id> when all four
 *   dimensions are set.
 *
 * Length and Texture entry points are intentionally NOT cascaded — their
 * tiles keep the hardcoded `/collections/<handle>?via={lengths|textures}`
 * link so a click lands directly on the underlying collection page.
 *
 * Activates only when the landing-cards wrapper has [data-shop-by-page].
 */

(function () {
  'use strict';

  function localPath(path) {
    var routes = window.Shopify && window.Shopify.routes;
    var root = (routes && routes.root) || document.documentElement.dataset.rootUrl || '/';
    return root.replace(/\/$/, '') + path;
  }


  var DIMENSION_TO_PAGE = {
    method: localPath('/pages/shop-by-method'),
    category: localPath('/pages/shop-by-shade'),
    texture: localPath('/pages/shop-by-texture'),
    length: localPath('/pages/shop-by-length')
  };

  // Pages where tile hrefs should be left as-is (clicks go to /collections/...).
  // All four entry points now go directly to the underlying collection so the
  // landing-tile URL matches the header menu (no cascading shop-by flow).
  var DIRECT_TO_COLLECTION_PAGES = ['method', 'category', 'length', 'texture'];

  var DIMENSION_LABEL = {
    method: 'Method',
    category: 'Shade',
    texture: 'Texture',
    length: 'Length'
  };

  function readSelection(params) {
    var sel = {};
    ['method', 'category', 'texture', 'length'].forEach(function (d) {
      var v = params.get(d);
      if (v) sel[d] = v;
    });
    return sel;
  }

  function selectionQuery(selection) {
    var parts = [];
    Object.keys(selection).forEach(function (k) {
      if (selection[k]) parts.push(encodeURIComponent(k) + '=' + encodeURIComponent(selection[k]));
    });
    return parts.length ? ('?' + parts.join('&')) : '';
  }

  function nextDimension(selection, currentDimension, order) {
    for (var i = 0; i < order.length; i++) {
      var d = order[i];
      if (d === currentDimension) continue;
      if (!selection[d]) return d;
    }
    return null;
  }

  function buildTileHref(catalog, currentDimension, tileValue, baseSelection) {
    var newSelection = Object.assign({}, baseSelection);
    newSelection[currentDimension] = tileValue;

    var unselected = nextDimension(newSelection, null, catalog.ORDER);
    if (unselected) {
      return Promise.resolve(DIMENSION_TO_PAGE[unselected] + selectionQuery(newSelection));
    }

    return catalog.findUniqueProducts(newSelection).then(function (products) {
      if (products.length === 0) return null;
      if (products.length === 1) {
        return localPath('/products/') + products[0].handle + '?variant=' + products[0].variantId;
      }
      var p = products[0];
      return localPath('/products/') + p.handle + '?variant=' + p.variantId;
    });
  }

  function renderBreadcrumb(wrapper, selection, currentDimension) {
    var entries = Object.keys(selection).filter(function (k) {
      return k !== currentDimension && selection[k];
    });
    if (entries.length === 0) return;

    var bar = document.createElement('div');
    bar.className = 'conde-shop-by-breadcrumb';

    var label = document.createElement('span');
    label.className = 'conde-shop-by-breadcrumb__label';
    label.textContent = 'Filtering by:';
    bar.appendChild(label);

    entries.forEach(function (k) {
      var chip = document.createElement('span');
      chip.className = 'conde-shop-by-breadcrumb__chip';
      chip.textContent = (DIMENSION_LABEL[k] || k) + ': ' + String(selection[k]).replace(/-/g, ' ');
      bar.appendChild(chip);
    });

    var clear = document.createElement('a');
    clear.className = 'conde-shop-by-breadcrumb__clear';
    clear.href = window.location.pathname;
    clear.textContent = 'Clear';
    bar.appendChild(clear);

    wrapper.insertBefore(bar, wrapper.firstChild);
  }

  function renderEmpty(grid, selection) {
    var msg = document.createElement('div');
    msg.className = 'conde-shop-by-empty';

    var p = document.createElement('p');
    p.className = 'conde-shop-by-empty__message';
    p.textContent = 'No products match these filters.';
    msg.appendChild(p);

    var reset = document.createElement('a');
    reset.className = 'conde-shop-by-empty__reset';
    reset.href = window.location.pathname;
    reset.textContent = 'Reset filters';
    msg.appendChild(reset);

    grid.appendChild(msg);
  }

  function init() {
    var wrapper = document.querySelector('.conde-landing-cards[data-shop-by-page]');
    if (!wrapper) return;
    var catalog = window.CondeShopByCatalog;
    if (!catalog) return;

    var currentDimension = wrapper.getAttribute('data-shop-by-page');
    var skipHrefRewrite = DIRECT_TO_COLLECTION_PAGES.indexOf(currentDimension) !== -1;
    var params = new URLSearchParams(window.location.search);
    var selection = readSelection(params);

    var grid = wrapper.querySelector('.conde-landing-cards__grid');
    var cards = grid ? Array.prototype.slice.call(grid.querySelectorAll('.conde-landing-card[data-dimension-value]')) : [];

    cards.forEach(function (card) {
      card.classList.add('conde-landing-card--pending');
      card.setAttribute('aria-disabled', 'true');
    });

    catalog.getAvailableValues(currentDimension, selection).then(function (available) {
      var visibleCount = 0;
      var hrefPromises = [];

      cards.forEach(function (card) {
        var value = card.getAttribute('data-dimension-value');
        if (!available.has(value)) {
          card.hidden = true;
          card.classList.add('conde-landing-card--hidden');
          card.classList.remove('conde-landing-card--pending');
          return;
        }
        visibleCount++;

        // On length / texture pages: leave the hardcoded tile href intact
        // (e.g. /collections/18-inch?via=lengths). These pages don't cascade
        // through the rest of the shop-by flow — clicking goes straight to
        // the underlying collection.
        if (skipHrefRewrite) {
          card.classList.remove('conde-landing-card--pending');
          card.removeAttribute('aria-disabled');
          return;
        }

        var p = buildTileHref(catalog, currentDimension, value, selection).then(function (href) {
          if (href) {
            card.setAttribute('href', href);
          } else {
            card.hidden = true;
            card.classList.add('conde-landing-card--hidden');
          }
          card.classList.remove('conde-landing-card--pending');
          card.removeAttribute('aria-disabled');
        });
        hrefPromises.push(p);
      });

      renderBreadcrumb(wrapper, selection, currentDimension);

      return Promise.all(hrefPromises).then(function () {
        if (visibleCount === 0 && grid) {
          renderEmpty(grid, selection);
        }
      });
    }).catch(function (err) {
      cards.forEach(function (card) {
        card.classList.remove('conde-landing-card--pending');
        card.removeAttribute('aria-disabled');
      });
      // Non-fatal: leave default tile hrefs intact so the page still works.
      console.error('Conde Shop-By init failed:', err);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
